import type { ReactNode } from 'react'
import {
  BgColorsOutlined,
  BuildOutlined,
  EllipsisOutlined,
  ExperimentOutlined,
  FileSearchOutlined,
  PushpinOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  ThunderboltOutlined,
} from '../../components/icons'
import { Card } from '../../components/ui/Card'
import { Chip } from '../../components/ui/Chip'
import type { RfqRequestedServiceView } from './domain'
import { SERVICE_TYPE_OPTIONS } from './review-domain'

/** UID-197 (ADR-0075 §2) — human labels for the FULL client catalog, plus the
 * one wire-only value (`'other'`) the catalog has no entry for. Deliberately
 * a plain (possibly-partial) `Record<string, string>` — NOT
 * `Record<RfqRequestedServiceView['type'], string>` — because that cast was
 * unsound: it asserted a total map over the read enum when the object's
 * actual keys are the 8-member catalog. Keying it loosely and reading it
 * through `serviceTypeLabel()` below makes the "no label for this key" state
 * representable and handled, instead of cast away. */
const SERVICE_TYPE_LABEL: Record<string, string> = {
  ...Object.fromEntries(SERVICE_TYPE_OPTIONS.map((option) => [option.value, option.label])),
  other: 'Other service',
}

/** Never lets a row render an empty type label (ADR-0075 §2 defensive
 * fallback): a known catalog/wire value resolves to its human label; any
 * other value — future wire enum growth the catalog hasn't caught up to yet,
 * or vice versa — falls back to a humanized version of the raw value rather
 * than `undefined`/blank. */
function serviceTypeLabel(type: string): string {
  const known = SERVICE_TYPE_LABEL[type]
  if (known) return known
  return humanize(type)
}

/** `'some_future_type'` -> `'Some future type'`. A safe, no-dependency
 * humanizer for the defensive label fallback — never throws, never returns
 * an empty string for a non-empty input. */
function humanize(raw: string): string {
  const words = raw.replace(/[_-]+/g, ' ').trim().toLowerCase()
  if (words === '') return 'Service'
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** UID-197 (ADR-0075 §2 design brief) — 34×34 purple icon chip per row.
 * Covers every catalog type + the wire `'other'` value; any unmapped/future
 * type falls back to the same decorative "ellipsis" glyph `'other'` uses, so
 * a row can never fail to render an icon. Decorative only (`aria-hidden`) —
 * the type label text is the accessible signal. */
const SERVICE_TYPE_ICON: Record<string, ReactNode> = {
  assembly: <BuildOutlined />,
  inserts: <PushpinOutlined />,
  cleanroom: <ExperimentOutlined />,
  finishing: <BgColorsOutlined />,
  inspection: <FileSearchOutlined />,
  certification: <SafetyCertificateOutlined />,
  expedite: <ThunderboltOutlined />,
  custom: <SettingOutlined />,
  other: <EllipsisOutlined />,
}

function serviceTypeIcon(type: string): ReactNode {
  return SERVICE_TYPE_ICON[type] ?? <EllipsisOutlined />
}

/** Human copy for the bounded `scope` enum. For `specific_parts` we surface the
 * bounded `sourceableIds` ONLY as an inert count — the ids are references to the
 * customer's own lines in THIS request (ADR-0062), rendered as text and never
 * dereferenced client-side. */
function scopeLabel(service: RfqRequestedServiceView): string {
  if (service.scope === 'entire_package') return 'Entire package'
  const count = service.sourceableIds?.length ?? 0
  if (count === 0) return 'Specific parts'
  return `Specific parts (${count} ${count === 1 ? 'part' : 'parts'})`
}

/** UID-197 (ADR-0075 §2) — static, client-side footer copy (not a wire
 * field), mirroring the house wording already used by `RFQ_BUNDLED_NOTE`.
 * INFERRED/RECONSTRUCTED per the Gate 2 UX spec handoff (DesignSync was
 * unavailable; built from the disclosed audit fallback) — flag for a later
 * design re-verification pass rather than treating this as literally
 * design-sourced. */
const RFQ_SERVICES_FOOTER_NOTE =
  "Services are quoted alongside your parts as one package — you'll see the " +
  'combined price and lead time once your quote is ready.'

export interface RfqDetailServicesCardProps {
  /** The RFQ's requested add-on services, read from `RfqView.requestedServices`
   * (UID-171 / ADR-0062 — bounded projection; the free-text `description` is
   * absent from the wire shape, so there is no XSS sink here by construction).
   * §4 (restoring `description` to this read surface) is HELD per ADR-0075 §4
   * — pending an explicit human product decision + security sign-off — and is
   * intentionally NOT implemented here. */
  readonly services?: readonly RfqRequestedServiceView[] | undefined
  /** RFQ-LEVEL "has a quote been returned yet?" (ADR-0075 §3). The card
   * renders its status tag PURELY from this prop — it never reads
   * `rfq.status`, fetches a quote, or reaches into context. The caller
   * (`RfqDetailPage`) derives it via `rfqIsQuoted(rfq.status)`. */
  readonly quoted: boolean
}

/**
 * UID-171 (ADR-0062) — the RFQ detail body's "Requested services" card (UX spec
 * §4). A read-only recap of the customer's requested add-on services, rendered as
 * a plain semantic `<ul>`/`<li>` of React-escaped text (bounded enums + an inert
 * part count) — no editable form, no per-service pricing, no free-text
 * description (ADR-0075 §4, HELD), and NO `dangerouslySetInnerHTML` (the
 * free-text `description` is not present on the read shape, and must never
 * become a render sink).
 *
 * UID-197 (ADR-0075) fidelity pass adds: the "SERVICES REQUESTED (N)" heading
 * (id-scoped CSS fix for the previously-undefined `.heading-text` bug), a
 * full-catalog type label with a defensive fallback, a 34×34 purple icon chip
 * per row, a right-aligned "Requested"/"Quoted with parts" status tag driven
 * purely by the `quoted` prop, and a static footer explanatory note.
 *
 * Empty/omitted state (§4.3): a request with zero services — OR an RFQ whose
 * services simply aren't read-surfaced — is a single, indistinguishable OMITTED
 * state: the card returns `null`. We never render "No services requested" when the
 * true answer might be "we don't know" (no false negative).
 */
export function RfqDetailServicesCard({ services, quoted }: RfqDetailServicesCardProps) {
  if (!services || services.length === 0) {
    return null
  }

  const tagLabel = quoted ? 'Quoted with parts' : 'Requested'
  const tagTone = quoted ? 'success' : 'purple'

  return (
    <Card aria-labelledby="rfq-detail-services-heading" className="rfq-detail-services-card">
      <h2 id="rfq-detail-services-heading" className="heading-text">
        Services requested ({services.length})
      </h2>
      <ul className="rfq-detail-services-list">
        {services.map((service, index) => (
          // Bounded projection has no stable id; the (type, scope, index) tuple is
          // a stable-enough key for a static, non-reorderable read-only recap.
          <li key={`${service.type}-${service.scope}-${index}`} className="rfq-detail-service">
            <span className="rfq-detail-service-icon" aria-hidden="true">
              {serviceTypeIcon(service.type)}
            </span>
            <span className="rfq-detail-service-text">
              <span className="rfq-detail-service-type">{serviceTypeLabel(service.type)}</span>
              <span className="rfq-detail-service-scope">{scopeLabel(service)}</span>
            </span>
            <Chip tone={tagTone} className="rfq-detail-service-tag">
              {tagLabel}
            </Chip>
          </li>
        ))}
      </ul>
      <p className="rfq-detail-services-note">{RFQ_SERVICES_FOOTER_NOTE}</p>
    </Card>
  )
}
